import { gql } from '@apollo/client';

// --- Queries ---

// Current staff admin identity. Returns { email, role, disabled }.
// Used by AdminRoleRoute for client-side gating; server still re-checks
// AdminRoleGuard on every staff* call (5s cache).
export const STAFF_ME = gql`
  query staffMe {
    staffMe {
      email
      role
      disabled
    }
  }
`;

// Staff Customers page — three independent search columns. Each rides an
// existing prod index (no new indexes added):
//   - Accounts → users.emails.address_1 (prefix or @domain) → dedupe by accountId
//   - Users    → users.emails.address_1 (prefix or @domain) → one row per user
//   - Devices  → Atlas Search `deviceSearch` (name/deviceName) + UUID_1 exact match
export const STAFF_SEARCH_ACCOUNTS = gql`
  query staffSearchAccounts($query: String!, $limit: Int = 25) {
    staffSearchAccounts(query: $query, limit: $limit) {
      accountId
      companyName
      companyEmail
    }
  }
`;

export const STAFF_SEARCH_USERS = gql`
  query staffSearchUsers($query: String!, $limit: Int = 25) {
    staffSearchUsers(query: $query, limit: $limit) {
      userId
      email
      accountId
      companyName
      companyEmail
    }
  }
`;

export const STAFF_SEARCH_DEVICES = gql`
  query staffSearchDevices($query: String!, $limit: Int = 25) {
    staffSearchDevices(query: $query, limit: $limit) {
      deviceId
      deviceName
      UUID
      accountId
      companyName
    }
  }
`;

// Company header + rollup device/fleet counts for the Customer drill-in page.
export const STAFF_GET_CUSTOMER = gql`
  query staffGetCustomer($accountId: String!) {
    staffGetCustomer(accountId: $accountId) {
      accountId
      companyName
      plan
      totalDevices
      onlineDevices
      staleDevices
      offlineDevices
      totalFleets
    }
  }
`;

// Cross-tenant device list for a single customer. Includes vpnIp / vpnRegion /
// vpnPublicKey / vpnCreatedAt joined from mdm-devices by UUID. Customer-side
// Device type never exposes these fields.
export const STAFF_CUSTOMER_DEVICES = gql`
  query staffCustomerDevices(
    $accountId: String!
    $filter: StaffDeviceFilterInput
    $pagination: PaginationInput
  ) {
    staffCustomerDevices(accountId: $accountId, filter: $filter, pagination: $pagination) {
      items {
        _id
        accountId
        teamId
        UUID
        deviceName
        status
        os
        osVersion
        model
        platform
        serialNo
        lastHeartBeat
        activationDate
        manufacturer
        localAppVersion
        tags
        tz
        usedStorage
        totalStorage
        vpnIp
        vpnRegion
        vpnPublicKey
        vpnCreatedAt
        fleetId
      }
      total
    }
  }
`;

// Fleet card grid for a single customer (read-only on staff side in Phase 1).
export const STAFF_CUSTOMER_FLEETS = gql`
  query staffCustomerFleets($accountId: String!) {
    staffCustomerFleets(accountId: $accountId) {
      _id
      name
      description
      tags
      isDefault
      deviceCount
      onlineCount
      staleCount
      offlineCount
      createdAt
      updatedAt
    }
  }
`;

// Employee WireGuard peers — mdm-devices rows where subnet != 'devices'.
// Grouped by subnet client-side.
export const STAFF_EMPLOYEE_PEERS = gql`
  query staffEmployeePeers($filter: StaffPeerFilterInput) {
    staffEmployeePeers(filter: $filter) {
      _id
      publicKey
      name
      ownerEmail
      vpnIp
      subnet
      region
      createdByRole
      createdAt
    }
  }
`;

// Reads network.yaml + joins ipam_counters for utilization per subnet.
export const STAFF_SUBNETS = gql`
  query staffSubnets {
    staffSubnets {
      name
      cidr
      type
      allowProvisioner
      used
      total
    }
  }
`;

// Full admin_users table — any staff role can read for transparency.
export const STAFF_ADMIN_USERS = gql`
  query staffAdminUsers {
    staffAdminUsers {
      _id
      email
      role
      invitedBy
      invitedAt
      lastLoginAt
      disabled
      createdAt
      updatedAt
    }
  }
`;

// --- Mutations (root-only; resolver re-checks caller.role === 'root') ---

export const STAFF_INVITE_ADMIN_USER = gql`
  mutation staffInviteAdminUser($email: String!, $role: AdminRole!) {
    staffInviteAdminUser(email: $email, role: $role) {
      _id
      email
      role
      invitedBy
      invitedAt
      lastLoginAt
      disabled
      createdAt
      updatedAt
    }
  }
`;

export const STAFF_UPDATE_ADMIN_USER_ROLE = gql`
  mutation staffUpdateAdminUserRole($email: String!, $role: AdminRole!) {
    staffUpdateAdminUserRole(email: $email, role: $role) {
      _id
      email
      role
      invitedBy
      invitedAt
      lastLoginAt
      disabled
      createdAt
      updatedAt
    }
  }
`;

export const STAFF_DISABLE_ADMIN_USER = gql`
  mutation staffDisableAdminUser($email: String!) {
    staffDisableAdminUser(email: $email) {
      _id
      email
      role
      invitedBy
      invitedAt
      lastLoginAt
      disabled
      createdAt
      updatedAt
    }
  }
`;

export const STAFF_REENABLE_ADMIN_USER = gql`
  mutation staffReenableAdminUser($email: String!) {
    staffReenableAdminUser(email: $email) {
      _id
      email
      role
      invitedBy
      invitedAt
      lastLoginAt
      disabled
      createdAt
      updatedAt
    }
  }
`;

// Raw wg-orchestrator peer document for a single device, keyed by UUID.
// Used by the MDM device JSON debug dialog. Staff-only (AdminRoleGuard).
// Non-staff callers receive 403 — the client should detect and hide the tab.
export const STAFF_DEVICE_PEER_RAW = gql`
  query staffDevicePeer($uuid: String!) {
    staffDevicePeer(uuid: $uuid) {
      _id
      publicKey
      name
      ownerEmail
      vpnIp
      subnet
      region
      createdByRole
      createdByTokenId
      createdAt
      updatedAt
    }
  }
`;
